// src/pages/EditResourcePage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, GeoPoint, collection, getDocs } from 'firebase/firestore';
import { useJsApiLoader } from '@react-google-maps/api';
import geohash from 'ngeohash';
import './EditResourcePage.css';

const Toast = ({ message, show }) => (show ? <div className="toast-notification">{message}</div> : null);

const CustomAlertModal = ({ message, onConfirm }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const handleConfirm = () => {
    if (dontShowAgain) localStorage.setItem('hasDismissedGpsAlert', 'true');
    onConfirm();
  };
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Location Data Updated</h2>
        <p style={{ whiteSpace: 'pre-line' }}>{message}</p>
        <div className="modal-checkbox">
          <input type="checkbox" id="dontShowAgain" checked={dontShowAgain} onChange={(e) => setDontShowAgain(e.target.checked)} />
          <label htmlFor="dontShowAgain">Don't show this message again</label>
        </div>
        <div className="modal-actions"><button className="btn-confirm-delete" onClick={handleConfirm}>OK</button></div>
      </div>
    </div>
  );
};

const CheckboxGroup = ({ title, options, selectedOptions, handleSelect }) => (
  <div className="checkbox-group-container">
    <p className="checkbox-group-title">{title}</p>
    <div className="checkbox-grid">
      {options.map((option) => (
        <label key={option.id} className="checkbox-label">
          <input 
            type="checkbox" 
            value={option.id} 
            checked={selectedOptions.includes(option.id)} 
            onChange={() => handleSelect(option.id)} 
          />
          {option.name}
        </label>
      ))}
    </div>
  </div>
);

function EditResourcePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoaded } = useJsApiLoader({ 
    id: 'google-map-script', 
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY, 
    libraries: ['geocoding'] 
  });
  
  const [formData, setFormData] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [resSnap, catSnap] = await Promise.all([
          getDoc(doc(db, 'resources', id)), 
          getDocs(collection(db, 'categories'))
        ]);
        
        const categoriesData = catSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setAllCategories(categoriesData);

        if (resSnap.exists()) {
          const data = resSnap.data();
          setFormData({
            ...data,
            organizationName: data.organizationName || '',
            message: data.message || '',
            categoryIDs: data.categoryIDs || [],
            location: data.location || '',
            city: data.city || '',
            state: data.state || '',
            latitude: data.coordinates?.latitude || '',
            longitude: data.coordinates?.longitude || '',
            geohash: data.geohash || ''
          });
        } else { 
          navigate('/dashboard'); 
        }
      } catch (err) {
        console.error("Error fetching resource:", err);
      } finally { 
        setLoading(false); 
      }
    };
    fetchAllData();
  }, [id, navigate]);

  const handleCategoryChange = (categoryId) => {
    setFormData(prev => {
      const currentIDs = prev.categoryIDs || [];
      const newIDs = currentIDs.includes(categoryId)
        ? currentIDs.filter(id => id !== categoryId)
        : [...currentIDs, categoryId];
      return { ...prev, categoryIDs: newIDs };
    });
  };

  const handleCalculateGps = () => {
    if (!formData.location) return alert('Enter an address first.');
    setIsCalculating(true);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: formData.location }, (results, status) => {
      setIsCalculating(false);
      if (status === 'OK') {
        const lat = results[0].geometry.location.lat();
        const lng = results[0].geometry.location.lng();
        const hash = geohash.encode(lat, lng);

        let extractedCity = '';
        let extractedState = '';
        results[0].address_components.forEach(component => {
          if (component.types.includes('locality')) {
            extractedCity = component.long_name;
          }
          if (component.types.includes('administrative_area_level_1')) {
            extractedState = component.short_name; 
          }
        });

        setFormData(prev => ({ 
          ...prev, 
          latitude: lat, 
          longitude: lng, 
          geohash: hash,
          city: extractedCity,
          state: extractedState
        }));

        if (localStorage.getItem('hasDismissedGpsAlert') !== 'true') {
          setAlertMessage(`Updated:\nLat: ${lat.toFixed(4)}\nLng: ${lng.toFixed(4)}\nCity: ${extractedCity}\nState: ${extractedState}`);
          setShowAlert(true);
        }
      } else { 
        alert('Geocode failed: ' + status); 
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.latitude || !formData.geohash) {
      return alert("Please calculate GPS and Geohash before saving.");
    }
    setIsSubmitting(true);
    try {
      const { latitude, longitude, geohash: hash, city, state, ...rest } = formData;
      const dataToUpdate = { 
        ...rest, 
        geohash: hash, 
        city: city,
        state: state,
        coordinates: new GeoPoint(parseFloat(latitude), parseFloat(longitude)) 
      };
      
      await updateDoc(doc(db, 'resources', id), dataToUpdate);
      setShowToast(true);
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) { 
      alert(error.message); 
      setIsSubmitting(false); 
    }
  };

  if (loading || !formData) return <div className="page-container">Loading...</div>;

  return (
    <div className="page-container">
      <Toast message="Changes Saved!" show={showToast} />
      {showAlert && <CustomAlertModal message={alertMessage} onConfirm={() => setShowAlert(false)} />}
      
      <div className="form-container">
        <h1 className="form-header">Edit Resource</h1>
        <form onSubmit={handleSubmit}>
          
          <div className="form-group">
            <label>Name of your Organization</label>
            <input 
              value={formData.organizationName} 
              onChange={e => setFormData({...formData, organizationName: e.target.value})} 
              className="form-input" 
              required 
            />
          </div>

          <div className="form-group">
            <label>More Details (Optional)</label>
            <textarea 
              value={formData.message} 
              onChange={e => setFormData({...formData, message: e.target.value})} 
              className="form-textarea"
              placeholder="Enter any additional details about the services provided..."
            />
          </div>

          <CheckboxGroup 
            title="Type of Resource (select all that apply)" 
            options={allCategories} 
            selectedOptions={formData.categoryIDs} 
            handleSelect={handleCategoryChange} 
          />

          <div className="form-group">
            <label>Location Address</label>
            <input 
              value={formData.location} 
              onChange={e => setFormData({...formData, location: e.target.value})} 
              className="form-input" 
              required 
            />
          </div>

          <div className="form-group-action">
            <button type="button" onClick={handleCalculateGps} className="calculate-gps-button" disabled={isCalculating}>
              {isCalculating ? 'Calculating...' : 'Calculate GPS & Geohash'}
            </button>
          </div>

          <div className="coordinate-group">
            <div className="form-group">
              <label>City</label>
              <input value={formData.city} readOnly className="form-input read-only-input" placeholder="Missing - Please calculate" />
            </div>
            <div className="form-group">
              <label>State</label>
              <input value={formData.state} readOnly className="form-input read-only-input" placeholder="Missing - Please calculate" />
            </div>
          </div>

          <div className="coordinate-group">
            <div className="form-group">
              <label>Lat</label>
              <input value={formData.latitude} readOnly className="form-input read-only-input" />
            </div>
            <div className="form-group">
              <label>Lng</label>
              <input value={formData.longitude} readOnly className="form-input read-only-input" />
            </div>
          </div>

          <div className="form-group">
            <label>Geohash (Required for Maps)</label>
            <input 
              value={formData.geohash} 
              readOnly 
              className="form-input read-only-input" 
              placeholder="Missing - Please calculate" 
            />
          </div>

          <button type="submit" className="submit-button" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default EditResourcePage;